import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare, faBell, faMessage, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { SEOHelmet } from 'components/SEO';
import { AssetUrls } from 'config/assets';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useUpdateUsernameMutation } from 'store/services/auth/authApi';
import { useGetPlayNotificationsQuery, useGetTournamentQuery, usePutPlayNotificationsMutation } from 'store/services/play/playApi';
import { ELITE_MONTHLY_PRICE_LABEL } from 'constants/elite';
import { isInviteCode, PLAY_BROWSER_PUSH_ENABLED } from 'constants/play';
import { validationPatterns } from 'utils/inputSanitization';
import { beginDiscordLogin, beginDiscordServerJoin } from 'utils/play/discordAuth';
import { discordServerStartUrl, DISCORD_JOIN_UNAVAILABLE, PLAY_ERROR_MESSAGES, playErrorCode, playErrorMessage } from 'utils/play/playErrors';
import { PlayLayout } from '../../layout/PlayLayout';
import { playerTabs } from '../../layout/playTabs';
import { usePlaySocket } from '../../hooks/usePlaySocket';
import { PlayToggle } from '../../components/PlayToggle';
import { PlaySheet } from '../../components/PlaySheet';
import { tournamentStatusLabel } from '../../playFormat';
import { DropSheet } from '../Player/sheets';
import '../Invite/InvitePage.css';
import '../Host/HostCreate.css';
import '../Host/HostLobby.css';
import './PlayProfilePage.css';

const PROFILE_PATH = '/play/me';
const discordIcon = faDiscord as IconProp;

/**
 * /play/me (Decisions 6 and 19, rulings E4 and E10, player Profile review):
 * identity row, Player name with inline Save, the Round alerts list (Discord
 * DM, Browser push behind PLAY_BROWSER_PUSH_ENABLED, SMS for Elite), Connected
 * accounts, and the outlined Drop from tournament while dropping is allowed.
 * Opened from a tournament (?from=code) it wears that tournament's header and
 * tab bar like the other player tabs.
 */
const PlayProfilePage: React.FC = () => {
  const [params] = useSearchParams();
  const from = params.get('from');
  const fromCode = isInviteCode(from ?? undefined) ? from : null;
  const joined = params.get('joined');
  const joinError = params.get('error');

  const { data: me } = useGetMeQuery();
  const { data: prefs, isLoading: prefsLoading } = useGetPlayNotificationsQuery();
  const [putPrefs, { isLoading: saving }] = usePutPlayNotificationsMutation();
  const [updateUsername, { isLoading: savingName }] = useUpdateUsernameMutation();
  const { data: tState } = useGetTournamentQuery(fromCode ?? '', { skip: !fromCode });
  usePlaySocket({ code: fromCode ?? '', enabled: !!fromCode && !!tState });

  const [username, setUsername] = useState('');
  const seeded = useRef(false);
  const [nameState, setNameState] = useState<'idle' | 'saved' | 'error'>('idle');
  const [nameError, setNameError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(joinError ? PLAY_ERROR_MESSAGES[joinError] ?? DISCORD_JOIN_UNAVAILABLE : null);
  // Eric's ruling (2026-10-30): a failed Discord DM opt-in shows a Try again button under the row error.
  const [discordRetry, setDiscordRetry] = useState<boolean>(!!joinError);
  const [eliteSheet, setEliteSheet] = useState(false);
  const [dropSheet, setDropSheet] = useState(false);

  useEffect(() => {
    if (!me || seeded.current) return;
    seeded.current = true;
    setUsername(me.username ?? '');
  }, [me]);

  const trimmed = username.trim();
  const nameChanged = !!me && trimmed !== me.username;
  const nameValid = validationPatterns.username.test(trimmed);

  const saveName = async () => {
    if (!nameChanged || !nameValid) return;
    setNameError(null);
    try {
      await updateUsername({ username: trimmed }).unwrap();
      setNameState('saved');
    } catch (err) {
      setNameState('error');
      setNameError(playErrorMessage(err, "Couldn't save that username."));
    }
  };

  const discordLinked = !!me?.discordId || !!prefs?.discordLinked;
  const discordAvailable = prefs?.discordConfigured !== false;
  const returnTo = fromCode ? `${PROFILE_PATH}?from=${fromCode}` : PROFILE_PATH;

  const onDiscordToggle = async (on: boolean) => {
    setRowError(null);
    setDiscordRetry(false);
    if (!prefs) return;
    const joinFailed = (message: string) => {
      setRowError(message);
      setDiscordRetry(true);
    };
    if (on && !prefs.discordGuildMember) {
      // No PUT first (review ruling): the join flow writes discordDm itself.
      const result = await beginDiscordServerJoin(returnTo, prefs.joinServerStartUrl);
      if (result !== 'redirecting') joinFailed(DISCORD_JOIN_UNAVAILABLE);
      return;
    }
    try {
      await putPrefs({ discordDm: on }).unwrap();
    } catch (err) {
      const startUrl = discordServerStartUrl(err);
      if (startUrl) {
        const result = await beginDiscordServerJoin(returnTo, startUrl);
        if (result !== 'redirecting') joinFailed(DISCORD_JOIN_UNAVAILABLE);
        return;
      }
      joinFailed(on ? playErrorMessage(err, DISCORD_JOIN_UNAVAILABLE) : playErrorMessage(err));
    }
  };

  const onPushToggle = async (on: boolean) => {
    setRowError(null);
    if (on) {
      if (typeof Notification === 'undefined') {
        setRowError("This browser doesn't support push notifications.");
        return;
      }
      const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (permission !== 'granted') {
        setRowError('Notifications are blocked for TradeMagic in this browser. Allow them in the site settings to turn this on.');
        return;
      }
    }
    try {
      await putPrefs({ push: on }).unwrap();
    } catch (err) {
      setRowError(playErrorMessage(err));
    }
  };

  const onSmsToggle = async (on: boolean) => {
    setRowError(null);
    if (!prefs) return;
    if (on && !prefs.isElite) {
      setEliteSheet(true);
      return;
    }
    try {
      await putPrefs({ sms: on }).unwrap();
    } catch (err) {
      const code = playErrorCode(err);
      if (code === 'ELITE_REQUIRED') {
        setEliteSheet(true);
        return;
      }
      setRowError(playErrorMessage(err));
    }
  };

  const connectDiscord = async () => {
    setRowError(null);
    const result = await beginDiscordLogin(returnTo);
    if (result === 'unavailable') setRowError(PLAY_ERROR_MESSAGES.DISCORD_NOT_CONFIGURED);
    else if (result === 'error') setRowError(PLAY_ERROR_MESSAGES.DISCORD_ERROR);
  };

  const tabs = fromCode ? playerTabs(fromCode, 'profile', tState?.tournament.decklistRequired ?? true) : undefined;
  const tournament = tState?.tournament;
  const canDrop = !!tState && tState.viewer.role === 'PLAYER' && !tState.viewer.dropped && tState.tournament.status !== 'COMPLETE';
  const headerTitle = tournament ? tournament.name : fromCode ? '' : 'Profile & alerts';
  const headerSub = tournament
    ? tState?.viewer.dropped
      ? 'Dropped'
      : tournamentStatusLabel(tournament.status, tState?.currentRound?.number ?? null, tState?.currentRound?.totalRounds ?? tournament.swissRounds)
    : undefined;
  const rowsDisabled = prefsLoading || saving;
  const discordSub = !discordLinked ? 'Connect Discord below to turn this on' : prefs?.discordGuildMember ? 'Pairings and round start' : 'Pairings and round start, after a quick server join';

  return (
    <PlayLayout title={headerTitle} subtitle={headerSub} backTo={fromCode ? undefined : '/play'} tabs={tabs}>
      <SEOHelmet title="Profile & alerts - TradeMagic Play" description="Your player name and round alerts." noindex />
      <div className="play-column play-column--18">
        <div className="play-profile__who" data-testid="play-profile-name">
          <span className="play-avatar-initial play-avatar-initial--viewer play-avatar-initial--large">{(me?.username ?? '?').slice(0, 1).toUpperCase()}</span>
          <div>
            <p className="play-profile__username">{me?.username ?? ''}</p>
            <p className="play-profile__auth">{discordLinked ? `Signed in with Discord${me?.discordUsername ? ` · @${me.discordUsername}` : ''}` : 'Signed in with TradeMagic'}</p>
          </div>
        </div>

        <section className="play-profile__section">
          <label className="play-field__label" htmlFor="play-profile-username">
            Player name
          </label>
          <div className="play-profile__name-row">
            <input
              id="play-profile-username"
              className="play-native-input"
              value={username}
              placeholder="Player name"
              maxLength={20}
              onChange={(e) => {
                setUsername(e.target.value);
                setNameState('idle');
              }}
              aria-invalid={nameChanged && !nameValid ? true : undefined}
            />
            <button
              type="button"
              className={`play-profile__save ${nameChanged && nameValid ? 'play-profile__save--active' : ''}`}
              disabled={!nameChanged || !nameValid || savingName}
              onClick={saveName}
              data-testid="play-profile-save"
            >
              {savingName ? 'Saving…' : nameState === 'saved' && !nameChanged ? 'Saved' : 'Save'}
            </button>
          </div>
          {nameChanged && !nameValid && <p className="play-field__status play-field__status--bad">3 to 20 letters, numbers, dashes or underscores</p>}
          {nameError && (
            <p className="play-field__status play-field__status--bad" role="alert">
              {nameError}
            </p>
          )}
        </section>

        <section className="play-profile__section">
          <p className="play-field__label">Round alerts</p>
          <div className="play-card play-card--list" data-testid="play-alerts">
            <div className="play-list-row" data-testid="alert-row-discord">
              <FontAwesomeIcon icon={discordIcon} className="play-list-row__icon" />
              <span className="play-list-row__main">
                <span className="play-list-row__label">Discord DM</span>
                <span className="play-list-row__sub">{discordSub}</span>
              </span>
              <PlayToggle checked={!!prefs?.discordDm} onChange={onDiscordToggle} disabled={rowsDisabled || !discordLinked || !discordAvailable} ariaLabel="Discord DM" />
            </div>
            {PLAY_BROWSER_PUSH_ENABLED && (
              <div className="play-list-row" data-testid="alert-row-push">
                <FontAwesomeIcon icon={faBell} className="play-list-row__icon" />
                <span className="play-list-row__main">
                  <span className="play-list-row__label">Browser push</span>
                  <span className="play-list-row__sub">Works while this tab is closed</span>
                </span>
                <PlayToggle checked={!!prefs?.push} onChange={onPushToggle} disabled={rowsDisabled} ariaLabel="Browser push" />
              </div>
            )}
            <div className="play-list-row" data-testid="alert-row-sms">
              <FontAwesomeIcon icon={faMessage} className="play-list-row__icon" />
              <span className="play-list-row__main">
                <span className="play-list-row__label">
                  SMS <span className="play-elite-tag">ELITE</span>
                </span>
                <span className="play-list-row__sub">{prefs?.isElite && !prefs.phoneVerified ? 'Verify your phone to get texts' : 'Texts the second pairings go up'}</span>
              </span>
              <PlayToggle checked={!!prefs?.sms} onChange={onSmsToggle} disabled={rowsDisabled} ariaLabel="SMS" />
            </div>
          </div>
          {prefs?.isElite && !prefs.phoneVerified && (
            <p className="play-helper play-profile__note">
              <Link to="/settings/account" className="play-text-button" style={{ padding: 0 }}>
                Verify your phone number
              </Link>{' '}
              to turn on texts.
            </p>
          )}
          {joined === '1' && <p className="play-field__status play-field__status--ok">Connected to the TradeMagic Discord server.</p>}
          {rowError && (
            <p className="play-error" role="alert" data-testid="play-alerts-error">
              {rowError}
            </p>
          )}
          {rowError && discordRetry && (
            <button
              type="button"
              className="play-secondary-button play-profile__retry"
              onClick={() => void onDiscordToggle(true)}
              disabled={rowsDisabled || !discordLinked || !discordAvailable}
              data-testid="play-alerts-retry"
            >
              Try again
            </button>
          )}
        </section>

        <section className="play-card play-card--list" data-testid="play-connected">
          <div className="play-list-row">
            <FontAwesomeIcon icon={discordIcon} className="play-list-row__icon play-list-row__icon--discord" />
            <span className="play-list-row__main">
              <span className="play-list-row__label">Discord</span>
            </span>
            {discordLinked ? (
              <span className="play-list-row__status play-list-row__status--ok">Connected</span>
            ) : (
              <button type="button" className="play-list-row__action" onClick={() => void connectDiscord()} disabled={!discordAvailable} data-testid="play-connect-discord">
                Connect
              </button>
            )}
          </div>
          <div className="play-list-row">
            <img src={AssetUrls.logos.iconLogo} alt="" className="play-list-row__logo" />
            <span className="play-list-row__main">
              <span className="play-list-row__label">TradeMagic account</span>
            </span>
            <span className="play-list-row__status">Connected</span>
          </div>
        </section>

        {canDrop && tournament && fromCode && (
          <button type="button" className="play-outline-danger-button" onClick={() => setDropSheet(true)} data-testid="play-profile-drop">
            <FontAwesomeIcon icon={faRightFromBracket} />
            Drop from tournament
          </button>
        )}
      </div>

      {dropSheet && tournament && fromCode && <DropSheet code={fromCode} tournamentId={tournament.id} onClose={() => setDropSheet(false)} />}

      {eliteSheet && (
        <PlaySheet ariaLabel="Get round alerts by text" onClose={() => setEliteSheet(false)} testId="play-elite-sheet">
          <span className="play-elite-tag play-elite-tag--large">ELITE</span>
          <h2 className="play-sheet-title">Get round alerts by text</h2>
          <p className="play-sheet-body">A text the second pairings go up, with your table and opponent. Included with TradeMagic Elite, along with your other Elite perks.</p>
          <Link to="/elite" className="play-primary-link" data-testid="elite-upgrade">
            Upgrade · {ELITE_MONTHLY_PRICE_LABEL}/mo
          </Link>
          <div className="play-sheet-drops">
            <a href="/elite" target="_blank" rel="noopener noreferrer" className="play-secondary-button">
              Learn more
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="play-secondary-button__external" />
            </a>
            <button type="button" className="play-secondary-button" onClick={() => setEliteSheet(false)}>
              Not now
            </button>
          </div>
        </PlaySheet>
      )}
    </PlayLayout>
  );
};

export default PlayProfilePage;
